import "./PhotoGallery.scss";
import React, { Fragment, useCallback, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Photos from "../../components/Photos/Photos";
import { photosCategoryInGallery } from "../../utils/config";
import { motion } from "framer-motion";
import {
  fetchPhotos,
  handlerShowAddPhotos,
  handlerOpenChangeSortPhotos,
  saveChangeSortPhotos,
} from "../../redux/Reducers/photoSlice";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { IPhoto, PhotoPostPage } from "../../types";
import { Button } from "../../components/Button";
import PreLoader from "../../components/PreLoader/PreLoader";
import { MetaData } from "../../helpers/MetaData";
import { animationTitleCategory } from "../../helpers/framerMotion";
import JsonLd from "../../helpers/JsonLD";
import { useWindowResize } from "../../hooks/useWindowResize";
import { markPrerenderError, markPrerenderReady, resetPrerenderStatus } from "../../utils/prerenderStatus";
import { RequestError } from "../../components/RequestError";
import { socialPreviews } from "../../utils/socialPreviews";

interface GalleryContent {
  title: string;
  description: string;
  sessionPath: string;
  sessionLinkLabel: string;
}

const galleryContent: Record<string, GalleryContent> = {
  newborn: {
    title: "Newborn Photography Gallery",
    description:
      "Explore gentle, timeless newborn portraits created for families in Charlotte, NC and surrounding communities. Each session preserves the tiny details, natural connection, and fleeting moments of your baby's first days.",
    sessionPath: "/prices/newborn/",
    sessionLinkLabel: "View newborn session details",
  },
  maternity: {
    title: "Maternity Photography Gallery",
    description:
      "Explore timeless maternity portraits celebrating the beauty and anticipation of motherhood in Charlotte, NC. Each pregnancy session captures this meaningful chapter with a relaxed, personal approach.",
    sessionPath: "/prices/maternity/",
    sessionLinkLabel: "View maternity session details",
  },
  baby: {
    title: "Baby Photography Gallery",
    description:
      "Explore joyful baby portraits created for families in Charlotte, NC and surrounding communities. Each milestone photography session preserves changing expressions, little details, and the moments your family treasures.",
    sessionPath: "/prices/baby/",
    sessionLinkLabel: "View baby session details",
  },
  family: {
    title: "Family Photography Gallery",
    description:
      "Explore relaxed family portraits created in Charlotte, NC and surrounding communities. Each photography session celebrates genuine connection, individual personalities, and the meaningful moments you share together.",
    sessionPath: "/prices/family/",
    sessionLinkLabel: "View family session details",
  },
  woman: {
    title: "Women’s Portrait Photography Gallery",
    description:
      "Explore elegant women’s portraits created in Charlotte, NC and surrounding communities. Each personal photography session celebrates confidence, individuality, and the beauty of every season of life.",
    sessionPath: "/contact/",
    sessionLinkLabel: "Ask about a portrait session",
  },
  discharge: {
    title: "Hospital Newborn Photography Gallery",
    description:
      "Explore tender hospital newborn portraits created for Charlotte-area families. These first-day photography sessions preserve your baby's earliest details and the quiet moments before your family heads home.",
    sessionPath: "/contact/",
    sessionLinkLabel: "Ask about a hospital session",
  },
  christening: {
    title: "Christening Photography Gallery",
    description:
      "Explore meaningful christening portraits created for families in Charlotte, NC and surrounding communities. Each photography session preserves the ceremony, family connections, and joyful details of your celebration.",
    sessionPath: "/contact/",
    sessionLinkLabel: "Ask about christening photography",
  },
};

const PhotoGallery: React.FC = () => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { width } = useWindowResize();
  const { showPhotos, getPhotos, loading, error, openChangeSortPhotos } = useAppSelector((state) => state.photos);
  const { user } = useAppSelector((state) => state.user);
  const typePhoto = photosCategoryInGallery.find((item) => pathname.includes(item)) || "newborn";
  const currentGallery = galleryContent[typePhoto];
  const galleryPreview =
    socialPreviews.gallery[typePhoto as keyof typeof socialPreviews.gallery] ?? socialPreviews.gallery.newborn;

  const loadGallery = useCallback(
    () => dispatch(fetchPhotos({ type: typePhoto, order: "sort" })),
    [dispatch, typePhoto]
  );

  useEffect(() => {
    let active = true;
    const prerenderSource = `gallery-${typePhoto}`;
    resetPrerenderStatus(prerenderSource);
    loadGallery()
      .unwrap()
      .then((photos) => {
        if (!active) return;
        photos.length > 0 ? markPrerenderReady(prerenderSource) : markPrerenderError(`${prerenderSource}-empty`);
      })
      .catch(() => active && markPrerenderError(`${prerenderSource}-request`));

    return () => {
      active = false;
    };
  }, [loadGallery, typePhoto]);

  const addPhotos = (photos: IPhoto[]): IPhoto[] => {
    if (width >= 1025) {
      return getPhotos.slice(0, photos.length + 4);
    }
    if (width >= 769) {
      return getPhotos.slice(0, photos.length + 3);
    }
    if (width >= 320) {
      return getPhotos.slice(0, photos.length + 2);
    }
    return getPhotos;
  };

  const handlerClickAddPhotos = (): void => {
    dispatch(handlerShowAddPhotos(addPhotos(showPhotos)));
  };

  const handlerHideButton = showPhotos.length === getPhotos.length;

  const handleChangeSortPhotos = (): void => {
    if (!openChangeSortPhotos) {
      dispatch(handlerOpenChangeSortPhotos(true));
    } else {
      dispatch(saveChangeSortPhotos(showPhotos));
      dispatch(handlerOpenChangeSortPhotos(false));
    }
  };

  const webPageDataGallery = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `https://alenalobacheva.com${pathname}#webpage-gallery`,
    name: `${currentGallery.title} | Alena Lobacheva Photography`,
    description: currentGallery.description,
    image: galleryPreview.imageOG,
    url: `https://alenalobacheva.com${pathname}`,
    potentialAction: {
      "@type": "ReadAction",
    },
  };

  const localBusinessDataGallery = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://alenalobacheva.com/#localbusiness",
    name: "Alena Lobacheva Photography",
    description: "Newborn, maternity, baby, and family photography in Charlotte, NC and surrounding communities.",
    image: galleryPreview.imageOG,
    url: "https://alenalobacheva.com/",
    telephone: "+1-516-468-4837",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Charlotte",
      addressRegion: "NC",
      addressCountry: "US",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+1-516-468-4837",
      contactType: "customer support",
    },
    sameAs: [
      "https://www.instagram.com/lobachevaphotography/",
      "https://www.facebook.com/Alen4ikLobacheva?mibextid=9R9pXO",
      "https://www.tiktok.com/@lobachevaphotography/",
    ],
  };

  return (
    <Fragment>
      <JsonLd data={webPageDataGallery} />
      <JsonLd data={localBusinessDataGallery} />
      <MetaData
        title={`${currentGallery.title} | Charlotte, NC`}
        description={currentGallery.description}
        canonicalLink={`https://alenalobacheva.com${pathname}`}
        {...galleryPreview}
        imageAltOG={`${typePhoto} photography`}
        titleOG={`${currentGallery.title} | Alena Lobacheva Photography`}
        descriptionOG={currentGallery.description}
      />
      <motion.section className="photoGallery" id="photoGallery" initial="hidden" animate="visible">
        <motion.div className="photoGallery__intro" variants={animationTitleCategory}>
          <p className="photoGallery__eyebrow">Portfolio</p>
          <h1 className="photoGallery__title">{currentGallery.title}</h1>
          <p className="photoGallery__introText">{currentGallery.description}</p>
          <Link className="photoGallery__sessionLink" to={currentGallery.sessionPath}>
            {currentGallery.sessionLinkLabel} <span aria-hidden="true">→</span>
          </Link>
        </motion.div>
        {loading ? (
          <div style={{ height: "1000px" }}>
            <PreLoader />
          </div>
        ) : error ? (
          <RequestError
            message="We couldn’t load this gallery. Check your connection and try again."
            onRetry={loadGallery}
          />
        ) : (
          <>
            {user.role.includes("ADMIN") && (
              <Button styleButton="ping" type="button" onClick={handleChangeSortPhotos}>
                {!openChangeSortPhotos ? "Изменить последовательность фотографий" : "Сохранить"}
              </Button>
            )}
            {showPhotos?.length > 0 && (
              <Photos
                onClick={handlerClickAddPhotos}
                hide={handlerHideButton}
                photoPostPage={PhotoPostPage.photoGalleryPage}
                buttonName="Show more photos"
              />
            )}
          </>
        )}
      </motion.section>
    </Fragment>
  );
};

export default PhotoGallery;
